import React from 'react';
import { aboutSection } from '../mockData';
import { motion } from 'framer-motion';

const AboutSection = () => {
  return (
    <section id="about" className="min-h-screen flex items-center justify-center relative overflow-hidden py-20">
      {/* Background gradient effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 md:w-[500px] md:h-[500px] bg-purple-600/20 rounded-full blur-[80px] md:blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-60 h-60 md:w-[400px] md:h-[400px] bg-cyan-600/15 rounded-full blur-[60px] md:blur-[100px]"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              {aboutSection.greeting}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Left: Enhanced Profile Image */}
            <motion.div
              className="lg:col-span-2 flex justify-center lg:justify-start order-2 lg:order-1"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="relative transform scale-90 md:scale-100">
                {/* Outer glow ring */}
                <div className="absolute -inset-4 bg-gradient-to-br from-cyan-500/30 via-purple-500/20 to-blue-600/30 rounded-full blur-2xl animate-pulse"></div>

                {/* Main container card */}
                <div className="relative w-72 h-72 md:w-96 md:h-96 rounded-3xl bg-gradient-to-br from-gray-900/80 to-gray-800/60 backdrop-blur-xl border border-cyan-400/20 shadow-2xl shadow-cyan-500/10 flex items-center justify-center p-6">

                  {/* Inner glow behind image */}
                  <div className="absolute inset-8 bg-gradient-to-br from-cyan-400/20 to-purple-600/20 rounded-full blur-xl"></div>

                  {/* Profile image container */}
                  <div className="relative">
                    {/* Gradient border ring */}
                    <div className="absolute -inset-2 bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 rounded-full animate-spin-slow opacity-70 blur-sm"></div>

                    {/* Static gradient ring */}
                    <div className="absolute -inset-1.5 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full"></div>

                    {/* Profile image circle */}
                    <div className="relative w-48 h-48 md:w-64 md:h-64 rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 flex items-center justify-center overflow-hidden shadow-lg shadow-cyan-500/30">
                      {/* Profile Image */}
                      <img
                        src="/assets/profile.jpg"
                        alt="K R Pavan Tej - DevOps & Cloud Engineer"
                        width={256}
                        height={256}
                        className="w-full h-full object-cover object-top rounded-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Decorative floating elements */}
                <motion.div
                  className="absolute -top-4 -right-4 w-8 h-8 bg-cyan-400/60 rounded-full blur-sm"
                  animate={{ y: [-5, 5, -5] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                ></motion.div>
                <motion.div
                  className="absolute -bottom-6 -left-6 w-12 h-12 bg-purple-500/40 rounded-full blur-md"
                  animate={{ y: [5, -5, 5] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                ></motion.div>
              </div>
            </motion.div>

            {/* Right: About Content - Wider text area */}
            <motion.div
              className="lg:col-span-3 space-y-6 text-center lg:text-left order-1 lg:order-2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-cyan-400">
                {aboutSection.title}
              </h3>

              <div className="space-y-4">
                {aboutSection.content.map((paragraph, index) => (
                  <motion.p
                    key={index}
                    className="text-gray-300 text-base md:text-lg leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </div>

              {/* Highlights Grid */}
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                {aboutSection.highlights.map((item, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-gray-900/60 to-gray-800/40 backdrop-blur-md border border-gray-700/50 rounded-xl p-5 hover:border-cyan-400/50 hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-300 group"
                  >
                    <p className="text-cyan-400 font-semibold text-sm mb-2 group-hover:text-cyan-300 transition-colors">
                      {item.label}
                    </p>
                    <p className="text-white font-medium text-base">{item.value}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
